/* eslint-disable */
import Node from './node.js';
import { PARAM_TYPE } from './connector.js';
import IdManager from './idManager.js';
import Group, { GROUP_TYPE } from './group.js';
import Sticky, { STICKY_TYPE } from './sticky.js';
import { NODE_TAGS } from './graphObject.js';
import Spline from './spline.js';
import { ENABLE_CACHING } from './constants.js';
import Utils from './utils.js';
import Utils3D from '../utils/utils3D.js';
import CoreUtils from '../../utils/coreUtils.js';

/**
 * A subnet is a collection of {@link GraphObject}s which may contain nodes, groups, and
 * potentially other items. A subnet may contain other subnets.
 * 
 * The output of the subnet is determined by a node which is marked as the output node
 * and glows blue in the editor UI. This can be changed interactively.
 * */
export default class Subnet extends Node
{
	constructor(pGraph, pParent)
	{
		super(pParent);
		this.parentGraph = pGraph;

		this.subnetId = CoreUtils.createGuid();

		this.addOutput(PARAM_TYPE.GEOMETRY, 'geo');	
		this.nodes = {};

		this.subnetPresets = [];

		// Also probably temp
		this.finalOutputNode = null;

		this.didCookAllAfterLoading = false;
	}

	getGraph()
	{
		return this.parentGraph;
	}

	getType()
	{
		return 'subnet';
	}

	reset()
	{
		this.nodes = {};
		this.setFinalOutputNode(null, false);
		this.markDirty();
	}

	dispose()
	{
		super.dispose();
		for(let key in this.nodes)
			this.nodes[key].dispose();
	}

	markDirty()
	{
		super.markDirty();

		// We can't call markDirty() since we get into infinite recursion
		if(this.finalOutputNode)
			this.finalOutputNode.isDirty = true;

		this.getGraph().onMarkDirty();
	}

	getLastError()
	{
		let strErrors = this.lastError;
		for(let key in this.nodes)
		{
			const node = this.nodes[key];
			if(!node.getLastError)
				continue;
			const currError = node.getLastError();
			if(currError.length > 0)
			{
				if(strErrors.length > 0)
					strErrors += '; ';
				strErrors += currError;
			}
		}
			
		return strErrors;
	}

	/*
	// Skip the warning for now because it's too annoying
	getLastWarning()
	{
		let strWarnings = this.lastWarning;
		for(let key in this.nodes)
		{
			const node = this.nodes[key];
			if(!node.getLastWarning)
				continue;
			const currError = node.getLastWarning();
			if(currError.length > 0)
			{
				if(strWarnings.length > 0)
					strWarnings += '; ';
				strWarnings += currError;
			}
		}
			
		return strWarnings;
	}
	*/

	getNode(iId)
	{
		if(iId === null || iId === undefined || iId < 0)
			return null;

		let pNode = this.nodes[iId];
		if(!pNode)
		{
			// Recursively find in our children
			for(let key in this.nodes)
			{
				let pCurrNode;
				if(this.nodes[key].getId() === iId)
					pCurrNode = this.nodes[key].getId();
				else if(this.nodes[key].getNode)
					pCurrNode = this.nodes[key].getNode(iId);
				
				if(pCurrNode)
				{
					pNode = pCurrNode;
					break;
				}
			}
		}

		return pNode;
	}

	getNodesOfType(nodeType)
	{
		let flatNodes = Object.values(this.nodes);
		let res = flatNodes.filter((node) => { return node.getType() === nodeType; });

		// Recurse
		flatNodes.forEach((node) => {
			if(node.getNodesOfType)
				res = res.concat(node.getNodesOfType(nodeType));
		});

		return res;
	}
	
	getNodes()
	{
		return this.nodes;
	}

	getNodesRecursive()
	{
		let res = Object.values(this.nodes).slice();
		for(let node of res)
		{
			if(node.getNodesRecursive)
				res = res.concat(node.getNodesRecursive());
		}
		return res;
	}

	getNodeTagged(strTag)
	{
		for(let key in this.nodes)
		{
			if(this.nodes[key].hasTag(strTag))
				return this.nodes[key];
		}

		return null;
	}

	getAllNodesTagged(strTag)
	{
		let resNodes = [];
		for(let key in this.nodes)
		{
			if(this.nodes[key].hasTag(strTag))
				resNodes.push(this.nodes[key]);
		}

		return resNodes;
	}

	getSubnetInputsNode()
	{
		return this.getNodeTagged(NODE_TAGS.SUBNET_INPUTS);
	}
	
	createNode(strType, iX = 0, iY = 0)
	{
		let pNewNode = this.createNodeRaw(strType);
		if(pNewNode.getContainsOtherNodes && pNewNode.getContainsOtherNodes())
		{
			let pInputsNode = pNewNode.createNode('subnetInputs', 400, 300);
			pInputsNode.addTag(NODE_TAGS.SUBNET_INPUTS);
		}
		pNewNode.setCenter(iX, iY);
		this.addNode(pNewNode);
		return pNewNode;
	}

	createGroup(iX = 0, iY = 0)
	{
		let pGroup = new Group(this);
		pGroup.setCenter(iX, iY);
		this.addNode(pGroup);
		return pGroup;
	}

	createGroupRaw()
	{
		return new Group(this);
	}

	createStickyRaw()
	{
		return new Sticky(this);
	}

	createNodeRaw(strType)
	{
		let pNewNode = this.parentGraph.getNodeTypeInfo(strType).generator(this);
		if(pNewNode.autoFillInputOptions)
			pNewNode.autoFillInputOptions();
		return pNewNode;
	}
	
	addNode(pNode)
	{
		this.nodes[pNode.getId()] = pNode;

		const bIsGroup = (pNode.getType() === GROUP_TYPE);
		const bIsSticky = (pNode.getType() === STICKY_TYPE);
		if(!this.finalOutputNode && !bIsGroup && !bIsSticky)
			this.setFinalOutputNode(pNode, false);
		pNode.setParent(this);
		this.markDirty();
	}

	getFinalOutputNode()
	{
		return this.finalOutputNode;
	}

	setFinalOutputNode(pNode, bMarkDirty = true)
	{
		this.finalOutputNode = pNode;
		if(bMarkDirty)
			this.markDirty();
	}
	
	createVisualizedCurve(scene, pResult)
	{
		if(!pResult || !pResult.points || pResult.isClosed === undefined)
			return;

		// Otherwise, it's a spline, visualize it:
		let pSpline = new Spline(pResult);
		let vecSize = pSpline.getSize();
		if(vecSize.z === undefined)
			vecSize.z = Number.MAX_VALUE;
		let pLinearPoints = pSpline.getLinearPoints(true, true, pSpline.getIs3D() ? null : 'xz');			
		let strGeneratedMeshName = this.finalOutputNode.getGeneratedMeshName();
		let path3d = BABYLON.MeshBuilder.CreateLines(strGeneratedMeshName, { points: pLinearPoints }, scene); 
		this.finalOutputNode.onMeshCreated(path3d);
		if(ENABLE_CACHING)
			path3d.setEnabled(true);

		// Create vertices
		let pFirstGeo = null;
		let iPointCount = 1;
		let pMesh;

		let fPointSize = 1.0;
		if(vecSize.x > 0.0 && vecSize.x < fPointSize)
			fPointSize = vecSize.x;
		if(vecSize.y > 0.0 && vecSize.y < fPointSize)
			fPointSize = vecSize.y;
		if(vecSize.z > 0.0 && vecSize.z < fPointSize)
			fPointSize = vecSize.z;
		fPointSize /= 25.0;

		fPointSize = CoreUtils.clamp(fPointSize, 0.003, 0.03);
		for(let vecPoint of pLinearPoints)
		{
			if(!pFirstGeo)
			{
				pFirstGeo = BABYLON.MeshBuilder.CreateSphere(`${strGeneratedMeshName}_pt0`, { 
					diameter: fPointSize, 
					segments: 2, 
					sideOrientation: BABYLON.Mesh.FRONTSIDE 
				}, scene);
				pFirstGeo.material = new BABYLON.StandardMaterial(strGeneratedMeshName, scene);
				pFirstGeo.material.diffuseColor = new BABYLON.Color3(255.0/255.0, 255/255, 255.0/255.0);
				//pFirstGeo.material.alpha = 0.5;
				pFirstGeo.position = vecPoint.clone();
				pFirstGeo.visOnly = true;
				this.finalOutputNode.onMeshCreated(pFirstGeo);
				if(ENABLE_CACHING)
					pFirstGeo.setEnabled(true);
				continue;
			}

			pMesh = pFirstGeo.createInstance(`${strGeneratedMeshName}_pt${iPointCount}`);
			pMesh.position = vecPoint.clone();
			pMesh.visOnly = true;
			this.finalOutputNode.onMeshCreated(pMesh);
			if(ENABLE_CACHING)
				pMesh.setEnabled(true);

			iPointCount++;
		}
	}

	visualizeFinalNodeResult(scene)
	{
		const finalOutputNode = this.getFinalOutputNode();
		if(!finalOutputNode)
			return;

		const visualizedOutputName = finalOutputNode.getVisualizedOutputName();
		if(!visualizedOutputName)
			return;

		let visMeshList = [];
		let lastResult = this.lastOutputResults[visualizedOutputName];
		let resultType = Array.isArray(lastResult) ? Utils.getObjectParameterType(lastResult[0]) : Utils.getObjectParameterType(lastResult);
		let strMeshOutputName = null;
		if(resultType == PARAM_TYPE.GEOMETRY)
		{
			visMeshList = lastResult;
			strMeshOutputName = visualizedOutputName;
		}
		else if(resultType == PARAM_TYPE.BBOX)
		{
			let parent = new BABYLON.Mesh(finalOutputNode.getGeneratedMeshName(), scene);
			parent.setBoundingInfo(new BABYLON.BoundingInfo(lastResult.minimumWorld, lastResult.maximumWorld));
			parent.showBoundingBox = true;
			finalOutputNode.onMeshCreated(parent);

			if(ENABLE_CACHING)
				parent.setEnabled(true);
		}
		else if(resultType == PARAM_TYPE.SPLINE)
		{
			if(Array.isArray(lastResult))
				lastResult.forEach((currValue) => { this.createVisualizedCurve(scene, currValue); });
			else
				this.createVisualizedCurve(scene, lastResult);
		}

		for(let outputName in this.lastOutputResults)
		{
			if(outputName === strMeshOutputName)
				continue;

			let pResult = this.lastOutputResults[outputName];
			if(Array.isArray(pResult) && Utils.getObjectParameterType(pResult[0]) == PARAM_TYPE.GEOMETRY)
			{
				for(let mesh of pResult)
				{
					// See if it's also part of our visualized result - this can happen with
					// Merge nodes.
					if(!visMeshList.find(m => m.uniqueId === mesh.uniqueId))
					{
						mesh.dontVisualize = true;
						mesh.setEnabled(false);
					}
				}
			}
		}
	}

	generate(scene, optStrOutputName)
	{
		this.markClean();
		
		super.generate(scene, null);

		// Visualize any curves that might be the final output of the node
		// We only allow that in Mobius, not when running in another environment
		// TODO: Don't do this in Norma, but don't break exported meshes that would become visible (staircase cutters)
		if(!this.getParent())
			this.visualizeFinalNodeResult(scene);

		if(ENABLE_CACHING && !this.didCookAllAfterLoading && !this.getGraph().getIsLoading())
		{
			// Cook any nodes that may not have been cooked above. This
			// can happen if some are behind a switch node and remain perpetually
			// dirty, causing more recooking than necessary.
			let bDidCookAnyNodes = false;
			this.didCookAllAfterLoading = true;
			for(let iNodeId in this.nodes)
			{
				if(this.nodes[iNodeId].getIsDirty())
				{
					this.nodes[iNodeId].generate(scene);
					bDidCookAnyNodes = true;
				}
			}

			if(bDidCookAnyNodes)
				this.markDirty();
		}

		// We moved this here from before the last cook block because adding them to an array
		// and setting them later takes a very long time for cases where there is a large number
		// of meshes, like roof tiles, simply due to array concatennation.
		if(ENABLE_CACHING && !this.getParent())
		{
			// Now, make any meshes for the final node visible:
			const bIsLoading = this.getGraph().getIsLoading();
			for(let outputName in this.lastOutputResults)
			{
				let pResult = this.lastOutputResults[outputName];
				if(!pResult)
					continue;

				if(!Array.isArray(pResult))
					continue;

				if(!Utils.getIsMeshObject(pResult[0]))
					continue;

				// Ensure that if we hit a parent object with hidden
				// children, we also unhide the child meshes
				for(let pMesh of pResult)
				{
					let pMeshesToProcess = Utils3D.getMeshesChildren([pMesh]);
					// We must only enable them at the very end
					for(let pMesh of pMeshesToProcess)
					{
						if(!pMesh.dontVisualize)
							pMesh.setEnabled(!bIsLoading);
						pMesh.freezeWorldMatrix();
					}
				}
			}
		}

		if(optStrOutputName)
			return this.lastOutputResults[optStrOutputName];
		else
			return this.lastOutputResults;
	}

	// We might need to change this since currently,
	// this means changing the final node in the subnet changes its outputs,
	// which could disconnect/corrupt some if wired in. We probably
	// need a dedicated outputs node.
	/*
	getOutputs()
	{
		if(this.finalOutputNode)
			return this.finalOutputNode.getOutputs();
		return this.outputs;
	}
	*/
	getOutputs(bGetEffectiveOutputs = false)
	{
		if(bGetEffectiveOutputs && this.finalOutputNode)
			return this.finalOutputNode.getOutputs(bGetEffectiveOutputs);
		return super.getOutputs();
	}

	generateSubclass(scene, inputValues, outputName, computedOutputResults)
	{
		if(this.finalOutputNode)
			return this.finalOutputNode.generate(scene, outputName);
		return null;
	}

	canUseMatchingInputForOutputResult(pOutput)
	{
		if(!super.canUseMatchingInputForOutputResult(pOutput))
			return false;

		// We can only do this if the output in question doesn't exist on our final output node.
		const doHaveSameOutputOnFinalNode = this.finalOutputNode.getOutput(pOutput.getName());
		return doHaveSameOutputOnFinalNode == null;
	}

	saveToObject()
	{
		let pRes = super.saveToObject();

		let pNodes = [];
		for(let iNodeId in this.nodes)
			pNodes.push(this.nodes[iNodeId].saveToObject());

		pRes['nodes'] = pNodes;
		pRes['finalOutputNodeId'] = this.finalOutputNode ? this.finalOutputNode.getId() : -1;
		pRes['subId'] = this.subnetId;

		if(this.subnetPresets.length > 0)
			pRes['subnetPresets'] = CoreUtils.clone(this.subnetPresets);

		return pRes;
	}

	loadFromObjectSubclass(pObj, pParent, bReset = true)
	{
		if(bReset)
			this.reset();

		let iMaxId = IdManager.getLoadingMaxId();

		let loadedIdsList = [];
		let pNodes = pObj['nodes'];

		// We need to sort the nodes so that subnets, procedural nodes, etc.,
		// are loaded after the others, since we need all non-remapped ids to be 
		// loaded first.
		pNodes.sort((nodeInfo1, nodeInfo2)  => {
			let value1 = (nodeInfo1['t'] === 'subnet' || nodeInfo1['t'] === 'proceduralAsset') ? 2 : 1;
			let value2 = (nodeInfo2['t'] === 'subnet' || nodeInfo2['t'] === 'proceduralAsset') ? 2 : 1;
			return value1 - value2;
		});
		for(let pNodeInfo of pNodes)
		{
			let pGraphItem;
			if(pNodeInfo['type'] == GROUP_TYPE)
				pGraphItem = Group.loadFromObject(pNodeInfo, this);
			else if(pNodeInfo['type'] == STICKY_TYPE)
				pGraphItem = Sticky.loadFromObject(pNodeInfo, this);
			else
				pGraphItem = Node.loadFromObject(pNodeInfo, this);
			this.addNode(pGraphItem);

			loadedIdsList.push(pGraphItem.getId());

			let iCurrMaxId = pGraphItem.getMaxId();
			if(iCurrMaxId > iMaxId)
			{
				iMaxId = iCurrMaxId;

				// We need to set the max id after every loaded node - 
				// since it may be a procedural asset with its own nodes,
				// some of which may conflict with other nodes in this list
				IdManager.setLoadedMaxId(iMaxId);
			}
		}

		if(pObj['subId'])
			this.subnetId = pObj['subId'];

		this.subnetPresets = [];
		if(pObj['subnetPresets'])
			this.subnetPresets = pObj['subnetPresets'];

		if(pObj['finalOutputNodeId'] !== undefined)
			this.setFinalOutputNode(this.getNode(pObj['finalOutputNodeId']), false);
		IdManager.setLoadedMaxId(iMaxId);

		return loadedIdsList;
	}

	deleteNodes(listNodeIds)
	{
		for(let iNodeId of listNodeIds)
		{
			let pNode = this.getNode(iNodeId);

			this.removeNodeFromAllGroups(iNodeId);

			if(this.finalOutputNode == pNode)
				this.setFinalOutputNode(null, false);
			if(pNode.disconnectAllInputsOutputs)
				pNode.disconnectAllInputsOutputs();
			delete this.nodes[pNode.getId()];
		}

		this.markDirty();

		if(!this.finalOutputNode)
		{
			let nodeIds = Object.keys(this.nodes);
			for(let nodeId of nodeIds)
			{
				if(this.nodes[nodeId].getType() === GROUP_TYPE || this.nodes[nodeId].getType() === STICKY_TYPE)
					continue;

				this.setFinalOutputNode(this.nodes[nodeId], false);
				break;
			}
		}
	}
	
	storeNodeCopyInfo(iNodeId, resDataList, mapStoredNodes)
	{
		if(mapStoredNodes[iNodeId])
			return;

		mapStoredNodes[iNodeId] = true;

		let pNode = this.getNode(iNodeId);
		resDataList.push(pNode.saveToObject());

		if(pNode.getType() === GROUP_TYPE)
		{
			// We need to add all of our contained nodes to the list, as well:
			const pGroupNodeIds = pNode.getNodes();
			for(let iCurrNodeId of Object.keys(pGroupNodeIds))
				this.storeNodeCopyInfo(iCurrNodeId, resDataList, mapStoredNodes);
		}
	}

	copyNodes(listNodeIds)
	{
		let mapStoredNodes = {};
		let resDataList = [];
		for(let iNodeId of listNodeIds)
			this.storeNodeCopyInfo(iNodeId, resDataList, mapStoredNodes);

		let finalData = {
			parentSubnetId: this.subnetId,
			nodes: resDataList
		};

		localStorage.setItem('__proc_edit_cliboard_buffer__', JSON.stringify(finalData));
	}

	pasteNodes(currentNodesBBox, offsetOverrides = null)
	{
		let pClipboardData = JSON.parse(localStorage.getItem('__proc_edit_cliboard_buffer__'));
		if(!pClipboardData)
			return null;

		let pNodeListData = pClipboardData['nodes'];
		if(pNodeListData.length === 0)
			return null;

		let iOffsetX = offsetOverrides?.x;
		let iOffsetY = offsetOverrides?.y;

		// For pasting multiple nodes, we want to make sure they're off to the 
		// side of the current nodes so they don't overlap:
		let pCenters = [];
		for(let pNodeInfo of pNodeListData)
			pCenters.push(new BABYLON.Vector2(pNodeInfo['c']['x'], pNodeInfo['c']['y']));

		let pastedNodesBBox = Utils3D.computePointsBBox(pCenters);
			
		if(iOffsetX === undefined)
		{
			if(currentNodesBBox.min.x !== Number.MAX_VALUE)
				iOffsetX = currentNodesBBox.max.x + 250 - pastedNodesBBox.min.x;
			else
				iOffsetX = -(pastedNodesBBox.min.x + pastedNodesBBox.max.x)/2.0;
		}

		if(iOffsetY === undefined)
		{
			if(currentNodesBBox.min.y !== Number.MAX_VALUE)
				iOffsetY = currentNodesBBox.max.y + 100 - pastedNodesBBox.min.y;
			else
				iOffsetY = -(pastedNodesBBox.min.y + pastedNodesBBox.max.y)/2.0;
		}

		let pCurrIdMap = {};
		this.remapIds(pNodeListData, pCurrIdMap, iOffsetX, iOffsetY, true, pClipboardData['parentSubnetId']);

		// Now go and load all these nodes
		return this.loadFromObjectSubclass({nodes: pNodeListData}, this.getParent(), false);
	}

	pasteFromLoadObject(pData)
	{
		let pCurrIdMap = {};
		this.remapIds(pData['nodes'], pCurrIdMap);

		// We also need to remap the final output node id
		const iFinalOutputId = pData['finalOutputNodeId'];
		if(pCurrIdMap[iFinalOutputId])
			pData['finalOutputNodeId'] = pCurrIdMap[iFinalOutputId];

		// Now go and load all these nodes
		const loadedIdList = this.loadFromObjectSubclass({nodes: pData['nodes']}, this.getParent(), false);

		// Set the final node
		this.setFinalOutputNode(this.getNode(pData['finalOutputNodeId']));

		return loadedIdList;
	}

	remapIdField(pInfo, strFieldName, pCurrIdMap, bResetMissing)
	{
		const iCurrId = parseInt(pInfo[strFieldName]);
		if(iCurrId < 0)
			return;

		const iIdInMap = pCurrIdMap[iCurrId];
		if(iIdInMap)
			pInfo[strFieldName] = iIdInMap;
		else if(bResetMissing)
			pInfo[strFieldName] = -1;
		else
		{
			pInfo[strFieldName] = IdManager.getNewId();
			pCurrIdMap[iCurrId] = pInfo[strFieldName];
		}
	}

	remapIds(pNodeListData, pCurrIdMap, fOffsetPositionX = 0, fOffsetPositionY = 0, bPreserveExternalInputs = false, strOriginalParentSubnetId = null)
	{
		// Do all of the nodes first so we know which ones we have
		for(let pNodeInfo of pNodeListData)
		{
			this.remapIdField(pNodeInfo, 'id', pCurrIdMap, false);

			// Offset a bit from the original position
			pNodeInfo['c']['x'] += fOffsetPositionX;
			pNodeInfo['c']['y'] += fOffsetPositionY;
		}

		// Then do the connections and prune any which weren't in the set.
		for(let pNodeInfo of pNodeListData)
		{
			// In case this is a group, rename its ids, as well:
			if(pNodeInfo['nodeIds'])
			{
				let pNewGroupMap = {};
				for(let iOldGroupId of Object.keys(pNodeInfo['nodeIds']))
				{
					let pTempObj = {id: iOldGroupId};
					this.remapIdField(pTempObj, 'id', pCurrIdMap, false);
					pNewGroupMap[pTempObj['id']] = true;
				}
				pNodeInfo['nodeIds'] = pNewGroupMap;
			}

			if(pNodeInfo['is'])
			{
				for(let pInputInfo of pNodeInfo['is'])
				{
					// If we have an input connected to a node that is not in our group and
					// which exists in the network, preserve it:
					if(bPreserveExternalInputs && strOriginalParentSubnetId === this.subnetId && !pCurrIdMap[pInputInfo['cni']] && this.getNode(pInputInfo['cni']))
					{
						// We need to add this new record to the source node connected to us:
						let pSourceNode = this.getNode(pInputInfo['cni']);
						pSourceNode.getOutput(pInputInfo['cno']).finishConnectionTo(pNodeInfo['id'], pInputInfo['n']);
						continue;
					}

					// Otherwise, remap
					this.remapIdField(pInputInfo, 'cni', pCurrIdMap, true);
				}
			}

			if(pNodeInfo['os'])
			{
				for(let pOutputInfo of pNodeInfo['os'])
				{
					for(let outputInfoIdx = pOutputInfo['cnis'].length - 1; outputInfoIdx >= 0; outputInfoIdx--)
					{
						let pConnectionInfo = pOutputInfo['cnis'][outputInfoIdx];
						this.remapIdField(pConnectionInfo, 'n', pCurrIdMap, true);
						if(pConnectionInfo['n'] == -1)
							pOutputInfo['cnis'].splice(outputInfoIdx, 1);
					}
				}
			}

			if(pNodeInfo['nodes'])
				this.remapIds(pNodeInfo['nodes'], pCurrIdMap, 0, 0, bPreserveExternalInputs, pNodeInfo['subId']);
		}
	}

	addUserInput(strName, optTypeName = PARAM_TYPE.ANY, parameterConfig = {}, bIsLoading = false, bAddOutput = true, bCheckInputsOnly = false)
	{
		let pOwnInput = super.addUserInput(strName, optTypeName, parameterConfig, bIsLoading, bAddOutput, bCheckInputsOnly);

		if(!bIsLoading)
		{
			// Add input to our input nodes
			// We may not have a subnet input for procedural asset, which is ok:
			if(this.getSubnetInputsNode())
			{
				let pSubnetInput = this.getSubnetInputsNode().addUserInput(strName, optTypeName, parameterConfig);
				pSubnetInput.setIsConnectorHidden(true);
			}
		}
		return pOwnInput;
	}

	removeNodeFromAllGroups(iNodeId)
	{
		for(let key in this.nodes)
		{
			let pNode = this.nodes[key];
			if(pNode.getType() !== GROUP_TYPE)
				continue;

			pNode.removeNode(iNodeId);
		}
	}

	// Determines whether the node is part of a list, including
	// a child of any group in that list.
	getIsPartOfGroupInList(iNodeId, pNodeIdList)
	{
		for(let iCurrNodeId of pNodeIdList)
		{
			if(this.nodes[iCurrNodeId].getType() !== GROUP_TYPE)
				continue;

			if(this.nodes[iCurrNodeId].doesContainObject(iNodeId))
				return true;
		}

		return false;
	}

	getContainsOtherNodes()
	{
		return true;
	}

	getReferencedAssetIds(resMapOut)
	{
		for(let key in this.nodes)
			this.nodes[key].getReferencedAssetIds(resMapOut);
	}

	getIsLoading()
	{
		for(let key in this.nodes)
		{
			if(this.nodes[key].getIsLoading())
				return true;
		}
		return false;
	}

	getMaxId()
	{
		let iMaxId = this.getId();
		for(let key in this.nodes)
		{
			let iCurrId = this.nodes[key].getMaxId();
			if(iCurrId > iMaxId)
				iMaxId = iCurrId;
		}

		return iMaxId;
	}

	dirtyAllUpstreamNodes()
	{
		let originalDirtyNodes = [];
		for(let key in this.nodes)
		{
			if(this.nodes[key].getIsDirty())
				originalDirtyNodes.push(this.nodes[key]);
		}

		// Now, mark all the nodes that the connect out to
		// recursively as dirty
		for(let pNode of originalDirtyNodes)
		{
			if(pNode.dirtyUpstreamChildren)
				pNode.dirtyUpstreamChildren();
			// Make sure we also dirty any contained nodes
			if(pNode.dirtyAllUpstreamNodes)
				pNode.dirtyAllUpstreamNodes();
		}
	}	
	
	dirtyUpstreamContainedNodes(strInputName)
	{
		const subnetInputsNode = this.getSubnetInputsNode();
		this.dirtyUpstreamContainedNodesStartingWith(subnetInputsNode, strInputName);
	}

	dirtyUpstreamContainedNodesStartingWith(pNode, strOutputName)
	{
		// Dirty the nodes connected to this input off of our main inputs node
		if(!pNode)
			return;

		let pOutput = pNode.getOutput(strOutputName);
		if(!pOutput)
			return;

		let iCookCount = this.getGraph().getCookCount();
		let pNodeInfos = pOutput.getConnectedNodeInfos();
		for(let pInfo of pNodeInfos)
		{
			let pCurrOutNode = this.getNode(pInfo.nodeId);
			if(!pCurrOutNode)
				continue;

			// We need to dirty this before the already dirty check because
			// the node could have been dirtied from a different node previously,
			// but with a different input name, in which case, if this is a procedural node,
			// we wouldn't dirty the insides at all. We could probably try to do some
			// optimization here, but it's not trivial since it is per-input, and profiling
			// before and after this change has not shown a great performance impact.
			pCurrOutNode.dirtyUpstreamContainedNodes(pInfo.inputName);
			if(pCurrOutNode.getIsAlreadyDirtyInternal(iCookCount))
				continue;

			pCurrOutNode.markDirtyInternal(iCookCount);
			pCurrOutNode.dirtyUpstreamChildren();
		}
	}

	getSubnetPresets()
	{
		return this.subnetPresets;
	}

	getNodeByName(strName)
	{
		for(let key in this.nodes)
		{
			let node = this.nodes[key];
			if(node.getName() == strName)
				return node;
		}

		return null;
	}

	getNodesForSubnetPreset()
	{
		let resNodes = [];
		for(let key in this.nodes)
		{
			let node = this.nodes[key];
			if(node.hasTag(NODE_TAGS.MAIN_PARAMS) || node.hasTag(NODE_TAGS.SUBCOMPONENT))
				resNodes.push(node);
		}

		return resNodes;
	}

	deleteSubnetPreset(strName)
	{
		for(let idx = this.subnetPresets.length - 1; idx >= 0; idx--)
		{
			if(this.subnetPresets[idx].name == strName)
				this.subnetPresets.splice(idx, 1);
		}
	}

	getSubnetPreset(strName)
	{
		for(let idx = this.subnetPresets.length - 1; idx >= 0; idx--)
		{
			if(this.subnetPresets[idx].name == strName)
				return this.subnetPresets[idx];
		}

		return null;
	}

	saveNewSubnetPreset(strName)
	{
		let presetInfo = {
			name: CoreUtils.replaceAll(strName, '"', "'"),
			nodePresets: {}
		};

		let presetNodes = this.getNodesForSubnetPreset();
		for(let node of presetNodes) {
			// Check to make sure the nodes don't have the same name, otherwise,
			// we're not going to save the preset correctly.
			if(presetInfo['nodePresets'][node.getName()])
				return false;
			presetInfo['nodePresets'][node.getName()] = { values: node.saveInputsAsPreset('')['values'], id: node.getId() };
		}

		this.subnetPresets.push(presetInfo);
		return true;
	}

	applySubnetPreset(strName)
	{
		let preset = this.getSubnetPreset(strName);

		// We need to handle cases where some of the presets may
		// change asset id values, which in turn causes procedural
		// nodes to be possibly reloaded and may change their inputs.
		// However, we don't know which of the saved values will do that,
		// since we may have anything connected to asset id inputs indirectly.
		// So we apply the presets once, then wait until everything has finished
		// loading, and then apply them again.
		const funcLoadTheRestResult = () => {
			for(let strNodeName of Object.keys(preset.nodePresets))
			{
				let pNode = this.getNodeByName(strNodeName);
				if(!pNode)
				{
					console.warn(`Could not find node for applying preset: ${strNodeName}`);
					continue;
				}
				pNode.applyPresetFromValues(preset.nodePresets[strNodeName]['values']);
			}
		};

		funcLoadTheRestResult();
		this.getGraph().getFinishedLoadingEvent().addObserverOnce(() => {
			funcLoadTheRestResult();
		});
	}

	onChildRenamed(node, strOldName, strNewName)
	{
		// See if this node is part of any global presets, and if so, update the name:
		for(let preset of this.subnetPresets)
		{
			if(!preset['nodePresets'][strOldName])
				continue;
			
			if(preset['nodePresets'][strNewName])
			{
				console.warn(`A node with the same name already exists in preset ${preset.name}, skipping rename logic`);
				continue;
			}
			
			preset['nodePresets'][strNewName] = preset['nodePresets'][strOldName];
			delete preset['nodePresets'][strOldName];
		}
	}
}